<template>
  <FormSection
    :title="$t('FINANCE.SALES_INVOICE.INVOICE_TITLE')"
    :description="$t('FINANCE.SALES_INVOICE.INVOICE_DESC')"
    :hint="$t('HINTS.FINANCE.SALES_INVOICE')"
  >
    <Card>
      <ElForm label-position="top">
        <ElFormItem :label="$t('DATE_RANGE')">
          <AlternativeSelect
            v-model="selectedRangeId"
            key-prop="id"
            label-prop="label"
            value-prop="id"
            :items="invoiceRangesWithLabels"
            :placeholder="$t('PICK_A_DATE_RANGE')"
          />
        </ElFormItem>

        <ErrorsList
          v-if="salesInvoiceFormErrors"
          :errors="salesInvoiceFormErrors"
        />

        <ElButton
          type="primary"
          native-type="submit"
          :loading="invoiceProcessing"
          :disabled="!selectedRangeId"
          @click.native.prevent="downloadInvoiceFile(selectedRangeId)"
        >
          <i class="material-icons">
            file_download
          </i>
          <span>{{ $t('DOWNLOAD_INVOICE') }}</span>
        </ElButton>

        <SalesReportsSendToEmailButton
          :title="$t('ADVERTISEMENT.FINANCE.INVOICE.SEND_TO_EMAIL')"
          :loading="isInvoiceSendingToEmail"
          @sendToEmail="sendToEmail"
        />
      </ElForm>
    </Card>
  </FormSection>
</template>

<script>
import FileSaver from 'file-saver';
import AlternativeSelect from '@@/common/AlternativeSelect';

import { Invoice } from '@/api/api';

import FormSection from '@@/common/layout/FormSection';
import Card from '@@/common/Card';

import ErrorsList from '@@/common/ErrorsList';
import { dateFormat } from '@/utils/date';
import lang from '@/mixins/lang';

import SalesReportsSendToEmailButton from './common/SalesReportsSendToEmailButton';

export default {
  name: 'SalesReportsInvoices',

  components: {
    FormSection,
    Card,
    ErrorsList,
    AlternativeSelect,
    SalesReportsSendToEmailButton,
  },

  mixins: [lang],

  data() {
    return {
      invoiceRanges: [],
      selectedRangeId: '',
      invoiceProcessing: false,
      isInvoiceSendingToEmail: false,
      salesInvoiceFormErrors: null,
    };
  },

  computed: {
    invoiceRangesWithLabels() {
      return this.invoiceRanges.map(item => ({
        ...item,
        label: `${this.translateDate(item.start_date)} - ${this.translateDate(
          item.end_date,
        )}`,
      }));
    },
  },

  created() {
    this.getInvoiceRanges();
  },

  methods: {
    getInvoiceRanges() {
      return Invoice.getInvoiceRange()
        .then(ranges => {
          this.invoiceRanges = ranges;
          this.selectedRangeId = ranges[0].id;
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    translateDate(date) {
      let toFormat = 'DD/MM/YYYY';

      if (this.lang === 'fa') {
        toFormat = 'jYYYY/jMM/jDD';
      }

      return dateFormat()(toFormat)(date);
    },

    downloadInvoiceFile(selectedRangeId) {
      this.invoiceProcessing = true;

      return Invoice.getInvoiceFile(selectedRangeId)
        .then(response => {
          const { noContent } = response;
          if (noContent) {
            const { message } = response;
            this.$message.error(message);
          } else {
            FileSaver.saveAs(response, 'sale_refund_invoice_file.zip');
            this.salesInvoiceFormErrors = null;
          }
        })
        .catch(({ body: { message, errors } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
          if (errors) {
            this.salesInvoiceFormErrors = [...errors];
          }
        })
        .then(() => {
          this.invoiceProcessing = false;
        });
    },

    sendToEmail(email) {
      this.isInvoiceSendingToEmail = true;

      return Invoice.sendInvoiceToEmail({
        id: this.selectedRangeId,
        email,
      })
        .then(() => {
          this.$message.success(
            this.$t('ADVERTISEMENT.FINANCE.SEND_TO_EMAIL.SUCCESSFUL'),
          );
        })
        .catch(({ message }) => {
          this.$message.error(message);
        })
        .finally(() => {
          this.isInvoiceSendingToEmail = false;
        });
    },
  },
};
</script>
